import { useState, useMemo, useCallback, memo, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  MenuItem,
  Select,
  CircularProgress,
  Button,
  Divider,
  InputLabel,
  Chip,
} from '@mui/material';
import { ArrowLeftIcon, PhoneIcon, CaretDownIcon } from '@phosphor-icons/react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  getOutboundFileData,
  triggerSingleOutboundCall,
} from '../../../utils/apis';
import ScheduleCallsModal from '../../modals/ScheduleCallsModal';
import { useDashboard } from '../../../hooks/useDashboard';
import { useAlert } from '../../../hooks/useAlert';
import MessagePopup from '../../common/MessagePopup';
import theme from '../../../theme';

interface Props {
  outboundFileId: string;
  onBack: () => void;
  agentId: string;
  agentKey: string;
  fileName?: string;
}

const commonSelectStyles = {
  height: '36px',
  '& .MuiSelect-select': {
    display: 'flex',
    padding: '8px 14px',
    alignItems: 'center',
    borderRadius: '6px',
    border: '1px solid #DCE0E7',
    background: '#F8F9FC',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 'normal',
    letterSpacing: '-0.14px',
    color: theme.palette.lightGray.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
};

const getStatusConfig = (status: string) => {
  const statusLower = status?.toLowerCase() ?? '';
  switch (statusLower) {
    case 'done':
      return { color: 'success' as const, label: 'Completed' };
    case 'resheduled':
      return { color: 'error' as const, label: 'Resheduled' };
    case 'not started':
      return { color: 'warning' as const, label: 'Not Started' };
    case 'call initiated':
      return { color: 'info' as const, label: 'Call Initiated' };
    default:
      return { color: 'default' as const, label: status || 'N/A' };
  }
};

const normalizePhone = (value: any): string => {
  const raw = String(value ?? '');
  return raw.replace(/\D/g, '');
};

const OutboundMappedData = memo(
  ({
    outboundFileId,
    onBack,
    agentId,
    agentKey,
    fileName = 'File Name',
  }: Props) => {
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [scheduleSelectedRecordIds, setScheduleSelectedRecordIds] = useState<
      string[]
    >([]);
    const [selectedProvider, setSelectedProvider] = useState('twilio');
    const [openScheduleCallsModal, setOpenScheduleCallsModal] = useState(false);
    const uiRowIdSeqRef = useRef(0);
    const uiRowIdByMongoIdRef = useRef<Record<string, string>>({});
    const uiRowIdByPhoneRef = useRef<Record<string, string>>({});
    const { alert, setAlert, showAlert } = useAlert();
    const [paginationModel, setPaginationModel] = useState({
      page: 0,
      pageSize: 100,
    });
    const [pendingCalls, setPendingCalls] = useState<Set<string>>(new Set());
    const { selectedAssistant } = useDashboard();

    const {
      data: outboundFileData,
      isLoading,
      refetch,
    } = useQuery({
      queryKey: [
        'outbound-file-data',
        outboundFileId,
        paginationModel.page,
        paginationModel.pageSize,
      ],
      queryFn: () =>
        getOutboundFileData({
          outboundFileId,
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
        }),
      enabled: !!selectedAssistant && !!outboundFileId,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      staleTime: 0,
      retry: 0,
      gcTime: 1000 * 60 * 30,
      placeholderData: keepPreviousData,
    });

    const queryClient = useQueryClient();

    const { mutate: triggerCall } = useMutation({
      mutationFn: async (variables: any) => {
        const response = await triggerSingleOutboundCall(variables);
        return { response, recordId: variables.recordId };
      },

      onMutate: useCallback((variables: any) => {
        setPendingCalls(prev => new Set(prev).add(variables.recordId));
      }, []),

      onSuccess: useCallback(
        (data: { response: any; recordId: string }) => {
          setPendingCalls(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.recordId);
            return newSet;
          });

          showAlert('Call triggered successfully', 'success');

          queryClient.invalidateQueries({
            queryKey: ['outbound-file-data', outboundFileId],
          });
        },
        [queryClient, outboundFileId, showAlert]
      ),

      onError: useCallback(
        (error: Error, variables: any) => {
          console.log(error);
          setPendingCalls(prev => {
            const newSet = new Set(prev);
            newSet.delete(variables.recordId);
            return newSet;
          });

          showAlert('Failed to trigger call', 'error');
        },
        [showAlert]
      ),
    });

    const handleCloseScheduleCallsModal = () => {
      setOpenScheduleCallsModal(false);
    };

    const fileData = useMemo(() => {
      const data = outboundFileData?.data?.data || [];

      const getOrCreateUiId = (row: any) => {
        const mongoId = row?._id ? String(row._id) : '';
        if (mongoId && uiRowIdByMongoIdRef.current[mongoId]) {
          return uiRowIdByMongoIdRef.current[mongoId];
        }

        const phoneKey = normalizePhone(
          row?.phoneNumber ?? row?.PhoneNumber ?? row?.PHONE_NUMBER
        );
        if (phoneKey && uiRowIdByPhoneRef.current[phoneKey]) {
          const existing = uiRowIdByPhoneRef.current[phoneKey];
          if (mongoId) uiRowIdByMongoIdRef.current[mongoId] = existing;
          return existing;
        }

        uiRowIdSeqRef.current += 1;
        const created = `ui-${outboundFileId}-${paginationModel.page}-${uiRowIdSeqRef.current}`;
        if (mongoId) uiRowIdByMongoIdRef.current[mongoId] = created;
        if (phoneKey) uiRowIdByPhoneRef.current[phoneKey] = created;
        return created;
      };

      const mapped = data.map((row: any) => ({
        ...row,
        id: getOrCreateUiId(row),
      }));

      return mapped;
    }, [outboundFileData?.data?.data, outboundFileId, paginationModel.page]);

    // Note: Do not add debug/network side effects here.

    const totalRows =
      outboundFileData?.data?.pagination?.totalPages *
        paginationModel.pageSize || 0;

    const allFields = useMemo(() => {
      if (!fileData.length) return [];
      const fields = new Set<string>();
      fileData.forEach((record: any) => {
        Object.keys(record).forEach((key: string) => fields.add(key));
      });
      return Array.from(fields);
    }, [fileData]);

    const reorderedFields = useMemo(() => {
      if (!allFields.length) return [];
      const fields = [...allFields];
      const statusIndex = fields.findIndex(
        field => field.toLowerCase() === 'status'
      );

      if (statusIndex !== -1) {
        const [statusField] = fields.splice(statusIndex, 1);
        fields.unshift(statusField);
      }

      return fields;
    }, [allFields]);

    const formatCellValue = (value: any): React.ReactNode => {
      if (value === null || value === undefined) return 'N/A';
      if (Array.isArray(value)) return JSON.stringify(value);
      if (typeof value === 'object') return JSON.stringify(value);
      return value;
    };

    const columns: GridColDef[] = [
      {
        field: '__check__',
        headerName: '',
        sortable: false,
        filterable: false,
        headerClassName: 'checkbox-header',
        cellClassName: 'checkbox-cell',
      },
      ...reorderedFields.map(field => ({
        field,
        headerName: field.toUpperCase(),
        headerClassName: 'data-grid-header',
        renderCell: (params: any) => {
          if (field.toLowerCase() === 'status') {
            const statusConfig = getStatusConfig(params.value);
            return (
              <Chip
                label={statusConfig.label}
                color={statusConfig.color}
                size="small"
                sx={{ fontWeight: 500, fontSize: '12px' }}
              />
            );
          }
          return formatCellValue(params.value);
        },
      })),
    ];

    columns.unshift({
      field: 'actions',
      headerName: 'Actions',
      pinnable: true,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: params =>
        selectedRows.includes(params.row.id) && (
          <IconButton
            color="primary"
            onClick={() => handleCallTrigger(params.row.id)}
            disabled={pendingCalls.has(params.row.id)}
            size="small"
          >
            {pendingCalls.has(params.row.id) ? (
              <CircularProgress size={20} />
            ) : (
              <PhoneIcon size={20} />
            )}
          </IconButton>
        ),
      cellClassName: 'actions-column',
      headerClassName: 'actions-column',
    });

    const handleCallTrigger = useCallback(
      (rowId: string) => {
        const selectedRowData = fileData.find((r: any) => r.id === rowId);
        if (!selectedRowData) return;
        if (!selectedRowData._id) {
          showAlert('Record id is not ready yet. Please try again.', 'error');
          return;
        }

        const phoneNumber = (selectedRowData.phoneNumber || '').replace(
          /\s+/g,
          ''
        );
        const userName =
          selectedRowData.UserName ||
          selectedRowData.userName ||
          selectedRowData.name ||
          selectedRowData.Name ||
          '';

        triggerCall({
          key: agentKey,
          DESTINATION_PHONE_NUMBER: phoneNumber,
          UserName: userName,
          // Single call scheduling also needs the real Mongo `_id`
          recordId: selectedRowData._id,
        });
      },
      [triggerCall, agentKey, fileData, showAlert]
    );

    const handleScheduleClick = () => {
      if (selectedRows.length === 0) {
        return;
      }

      const computeSelectedRecordIds = (rawRows: any[]) => {
        const mappedRows = rawRows.map((row: any) => {
          const mongoId = row?._id ? String(row._id) : '';
          if (mongoId && uiRowIdByMongoIdRef.current[mongoId]) {
            return { ...row, id: uiRowIdByMongoIdRef.current[mongoId] };
          }

          const phoneKey = normalizePhone(
            row?.phoneNumber ?? row?.PhoneNumber ?? row?.PHONE_NUMBER
          );
          if (phoneKey && uiRowIdByPhoneRef.current[phoneKey]) {
            const existing = uiRowIdByPhoneRef.current[phoneKey];
            if (mongoId) uiRowIdByMongoIdRef.current[mongoId] = existing;
            return { ...row, id: existing };
          }

          uiRowIdSeqRef.current += 1;
          const created = `ui-${outboundFileId}-${paginationModel.page}-${uiRowIdSeqRef.current}`;
          if (mongoId) uiRowIdByMongoIdRef.current[mongoId] = created;
          if (phoneKey) uiRowIdByPhoneRef.current[phoneKey] = created;
          return { ...row, id: created };
        });

        return selectedRows
          .map(id => mappedRows.find((r: any) => r.id === id)?._id ?? null)
          .filter((id: string | null): id is string => !!id);
      };

      const selectedRecordIdsInitial = computeSelectedRecordIds(
        outboundFileData?.data?.data || []
      );

      // If backend Mongo `_id` isn't ready yet, try a few refetches automatically.
      // This avoids requiring a manual refresh to populate IDs for scheduling.
      (async () => {
        let attempt = 0;
        let selectedRecordIdsNow = selectedRecordIdsInitial;
        const maxAttempts = 3;

        while (
          attempt < maxAttempts &&
          selectedRecordIdsNow.length === 0 &&
          selectedRows.length > 0
        ) {
          attempt += 1;
          const result = await refetch();
          const rawRows = result?.data?.data?.data || [];
          selectedRecordIdsNow = computeSelectedRecordIds(rawRows);
        }

        setScheduleSelectedRecordIds(selectedRecordIdsNow);
        setOpenScheduleCallsModal(true);
      })();
    };

    return (
      <Box
        sx={{
          width: '100%',
          position: 'relative',
          height: 'calc(100vh - 200px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
            backgroundColor: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 1000,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={onBack} sx={{ color: 'text.primary' }}>
              <ArrowLeftIcon weight="bold" />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {fileName}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <InputLabel>Provider</InputLabel>
            <Select
              value={selectedProvider}
              onChange={e => setSelectedProvider(e.target.value)}
              size="small"
              displayEmpty
              label="Provider"
              sx={{
                ...commonSelectStyles,
                minWidth: 120,
              }}
              IconComponent={CaretDownIcon}
            >
              <MenuItem value="twilio">Twilio</MenuItem>
              <MenuItem value="plivo">Plivo</MenuItem>
            </Select>

            <Button
              variant="contained"
              color="primary"
              startIcon={<PhoneIcon weight="bold" />}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                px: 3,
                height: '36px',
              }}
              onClick={handleScheduleClick}
            >
              Schedule Call
            </Button>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            '& .MuiDataGrid-root': {
              // border: '1px solid #E5E7EB',
              backgroundColor: 'white',
              height: '100% !important',
              '& .MuiDataGrid-main': {
                overflow: 'hidden',
                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'auto',
                },
              },
              '& .checkbox-header': {
                padding: '0 8px',
                justifyContent: 'center',
                position: 'sticky',
                bgcolor: '#F8F9FC',
              },
              '& .checkbox-cell': {
                padding: '0 8px',
                justifyContent: 'center',
              },
              '& .MuiCheckbox-root': {
                padding: '4px',
                color: 'secondary.text',
                '&.Mui-checked': {
                  color: 'primary.main',
                },
              },
              '& .MuiDataGrid-checkboxInput': {
                width: '20px',
                height: '20px',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: '#F9FAFB',
                },
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid #E5E7EB',
                backgroundColor: '#F9FAFB',
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
              },
              '& .MuiDataGrid-toolbarContainer': {
                padding: '12px 16px',
                borderBottom: '1px solid #E5E7EB',
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: 'white',
              },
              '& .checkbox-cell, .checkbox-header': {
                position: 'sticky',
                right: 0,
                backgroundColor: 'background.paper',
                borderLeft: '1px solid #E5E7EB',
                zIndex: 1,
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#F9FAFB',
                borderBottom: '1px solid #E5E7EB',
                position: 'sticky',
                top: 'var(--DataGrid-toolbarHeight)',
                zIndex: 3,
                fontWeight: 600,
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #E5E7EB',
            },
          }}
        >
          <DataGrid
            rows={fileData}
            columns={columns}
            getRowId={row => row.id}
            pagination
            paginationMode="server"
            rowCount={totalRows}
            pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            loading={isLoading}
            checkboxSelection
            onRowSelectionModelChange={newSelection => {
              setSelectedRows(prev => {
                const currentPageIds = fileData.map(
                  (r: { id: string }) => r.id
                );
                const withoutCurrentPage = prev.filter(
                  id => !currentPageIds.includes(id)
                );
                return [...withoutCurrentPage, ...(newSelection as string[])];
              });
            }}
            rowSelectionModel={selectedRows.filter(id =>
              fileData.some((r: { id: string }) => r.id === id)
            )}
            disableRowSelectionOnClick
            sx={{
              border: 'none',
              '--DataGrid-overlayHeight': '300px',
              '& .data-grid-header': {
                bgcolor: '#F8F9FC',
                color: 'custom.textBlue',
                fontSize: '14px',
                fontWeight: 600,
                padding: '12px 16px',
              },
              '& .MuiDataGrid-cell': {
                padding: '12px 16px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                '&:focus, &:focus-within': {
                  outline: 'none !important',
                },
              },
            }}
          />
        </Box>

        <MessagePopup alert={alert} setAlert={setAlert} />
        <ScheduleCallsModal
          open={openScheduleCallsModal}
          onClose={handleCloseScheduleCallsModal}
          selectedRows={scheduleSelectedRecordIds}
          agentId={agentId}
          agentKey={agentKey}
          onSuccess={() =>
            showAlert('Calls scheduled successfully!', 'success')
          }
          onError={errorMessage =>
            showAlert(
              errorMessage || 'Failed to schedule calls. Please try again.',
              'error'
            )
          }
        />
      </Box>
    );
  }
);

export default OutboundMappedData;
